import React, { useState, useEffect } from "react";
import { Modal, Input, List, Spin, Empty, message, Space } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { clonePageComponent } from "../../services/salesforceProjectApi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getEnv } from "../../config/getEnv";
import { storage } from "../../auth/storage";

export default function ClonePageComponentModal({
  visible,
  onClose,
  onSuccess,
  pageId,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);

  // Load components on mount or when pageId changes
  useEffect(() => {
    if (visible && pageId) {
      loadComponents();
    }
  }, [visible, pageId]);

  const loadComponents = async () => {
    if (!pageId) return;

    setLoading(true);
    try {
      // Note: You may need to add getPageComponents to salesforceProjectApi.js
      // For now, we'll use a direct API call
      const token = storage.readSfAuthSession()?.tokenSet?.access_token;
      const baseURL =
        getEnv("REACT_APP_SF_BASE_URL") ||
        getEnv("REACT_APP_SALESFORCE_INSTANCE_URL");

      const response = await axios.get(
        `${baseURL}/services/apexrest/ProjectPageComponent?ppid=${pageId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const components = response?.data?.data || response?.data || [];
      setResults(Array.isArray(components) ? components : []);
    } catch (error) {
      message.error("Failed to load page components");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filter results based on search
  const filteredResults = results.filter(
    (component) =>
      search.length < 1 ||
      component.Name?.toLowerCase().includes(search.toLowerCase()),
  );

  // Handle component selection
  const handleSelectComponent = (component) => {
    setSelectedComponent(component);
  };

  // Handle clone with server-side API
  const handleClone = async () => {
    if (!selectedComponent) {
      message.warning("Please select a page component to clone");
      return;
    }

    setCloning(true);
    try {
      const response = await clonePageComponent(selectedComponent.Id);

      if (response.success) {
        const clonedComponentId = response.data[0]?.Id;
        message.success(
          response.message || "Page Component cloned successfully!",
        );

        // Reset state
        setSearch("");
        setSelectedComponent(null);
        onClose();

        // Navigate to cloned component details
        if (clonedComponentId) {
          navigate(`/ppc/${clonedComponentId}`);
        }

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(response.data[0]);
        }
      } else {
        message.error(response.error?.message || "Failed to clone component");
      }
    } catch (error) {
      message.error(
        "Failed to clone component: " +
          (error.response?.data?.error?.message || error.message),
      );
      console.error("Clone error:", error);
    } finally {
      setCloning(false);
    }
  };

  const handleCancel = () => {
    setSearch("");
    setSelectedComponent(null);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <CopyOutlined />
          <span>Clone Page Component</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleClone}
      okText="Clone Component"
      okButtonProps={{
        disabled: !selectedComponent || cloning,
        loading: cloning,
      }}
      width={600}
      bodyStyle={{ padding: 16 }}
    >
      <div className="space-y-4">
        {/* Search Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select a page component to clone:
          </label>
          <Input
            placeholder="Filter by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            size="large"
            prefix={<CopyOutlined />}
          />
        </div>

        {/* Component Results */}
        <div
          style={{
            maxHeight: "400px",
            overflowY: "auto",
            border: "1px solid #f0f0f0",
            borderRadius: "4px",
            padding: "8px",
          }}
        >
          {loading ? (
            <div className="text-center py-8">
              <Spin tip="Loading page components..." />
            </div>
          ) : filteredResults.length > 0 ? (
            <List
              dataSource={filteredResults}
              renderItem={(item) => (
                <List.Item
                  className={`cursor-pointer transition-colors px-4 ${
                    selectedComponent?.Id === item.Id
                      ? "bg-blue-50"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => handleSelectComponent(item)}
                  style={{ cursor: "pointer" }}
                >
                  <List.Item.Meta
                    title={
                      <div className="flex items-center">
                        {selectedComponent?.Id === item.Id && (
                          <CopyOutlined className="mr-2 text-blue-500" />
                        )}
                        <span className="font-medium">{item.Name}</span>
                      </div>
                    }
                    description={
                      <div className="text-sm text-gray-500">
                        <div>Order: {item.Component_Order__c || "N/A"}</div>
                        {item.Project_Page__r?.Name && (
                          <div>Page: {item.Project_Page__r.Name}</div>
                        )}
                        {item.Headline_1__c && (
                          <div>Headline: {item.Headline_1__c}</div>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            <Empty description="No page components found" className="py-8" />
          )}
        </div>

        {/* Selected Component Info */}
        {selectedComponent && (
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <div className="text-sm">
              <div className="font-medium text-blue-900">
                Selected: {selectedComponent.Name}
              </div>
              <div className="text-blue-700 text-xs mt-1">
                A new component will be created with all content copied
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
